import React, { useState, useEffect } from "react";
import { ChatTab } from "@/components/chat/ChatTab";

type HelpTab = "support" | "chat";

export const HelpPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState<HelpTab>("support");

  // Force support tab if chat is selected (chat is disabled)
  useEffect(() => {
    if (activeTab === "chat") {
      setActiveTab("support");
    }
  }, [activeTab]);

  return (
    <div className="flex flex-col h-full">
      {/* Tab Navigation */}
      <div className="flex gap-2 mb-4 border-b border-custom-light-200">
        <button
          onClick={() => setActiveTab("support")}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === "support"
              ? "border-b-2 border-custom-navy text-custom-navy"
              : "text-custom-grey hover:text-custom-navy"
          }`}
        >
          Support
        </button>
        <button
          onClick={() => setActiveTab("chat")}
          disabled
          className={`px-4 py-2 font-medium transition-colors opacity-50 cursor-not-allowed ${
            activeTab === "chat"
              ? "border-b-2 border-custom-navy text-custom-navy"
              : "text-custom-grey"
          }`}
        >
          AI Chat
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === "support" && (
        <div className="space-y-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-custom-navy mb-2">
              Need Help?
            </h2>
            <p className="text-custom-grey">
              Contact our technical support team for assistance
            </p>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-xl p-6 border border-custom-light-200 shadow-sm">
            <h3 className="text-lg font-semibold text-custom-navy mb-4">
              Technical Support
            </h3>

            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-custom-light-50 rounded-lg border border-custom-light-200">
                <div className="w-10 h-10 bg-custom-navy rounded-full flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-custom-navy">Email Support</h4>
                  <a
                    href="mailto:elad@herocon.ai"
                    className="text-custom-navy hover:text-custom-dark transition-colors"
                  >
                    elad@herocon.ai
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-custom-navy">
                    Project Expansion
                  </h4>
                  <a
                    href="mailto:alon@herocon.ai"
                    className="text-green-600 hover:text-green-800 transition-colors"
                  >
                    alon@herocon.ai
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Common Issues */}
          <div className="bg-white rounded-xl p-6 border border-custom-light-200 shadow-sm">
            <h3 className="text-lg font-semibold text-custom-navy mb-4">
              Common Issues
            </h3>

            <div className="space-y-3">
              <div className="p-3 bg-custom-light-50 rounded-lg">
                <h4 className="font-medium text-custom-navy mb-1">
                  Model Not Loading
                </h4>
                <p className="text-sm text-custom-grey">
                  Ensure your model file is in a supported format (RVT, IFC,
                  NWD, etc.)
                </p>
              </div>

              <div className="p-3 bg-custom-light-50 rounded-lg">
                <h4 className="font-medium text-custom-navy mb-1">
                  Validation Errors
                </h4>
                <p className="text-sm text-custom-grey">
                  Check that your model contains the required elements for
                  compliance validation
                </p>
              </div>

              <div className="p-3 bg-custom-light-50 rounded-lg">
                <h4 className="font-medium text-custom-navy mb-1">
                  Performance Issues
                </h4>
                <p className="text-sm text-custom-grey">
                  Large models may take time to process. Consider optimizing
                  your model size
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Chat Tab */}
      {activeTab === "chat" && (
        <div className="flex-1 flex flex-col min-h-0">
          <ChatTab />
        </div>
      )}
    </div>
  );
};
